import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { User } from 'src/shared/types/user';
import {
  formatTimezoneLabel,
  getBrowserTimezone,
  getTimezoneOptions
} from 'src/shared/utils/timezone';

import { addToast } from 'src/shared/components/ui';
import {
  Autocomplete,
  AutocompleteItem,
  ButtonV2,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from 'src/shared/components/ui';

import { useUpdateUserProfile } from '../hooks/useUpdateUserProfile';
import { useUpdateUserProfileForm } from '../hooks/useUpdateUserProfileForm';
import { UserProfileFormValues } from '../models/schema';

interface AccountFormModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  user: User | null;
}

const AccountFormModal: React.FC<AccountFormModalProps> = ({
  isOpen,
  onOpenChange,
  user
}) => {
  // Users who never recorded a timezone are offered the browser's, so saving
  // the form records it.
  const defaultTimezone = user?.timezone ?? getBrowserTimezone();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useUpdateUserProfileForm({
    email: user?.email,
    first_name: user?.firstName,
    last_name: user?.lastName,
    username: user?.username,
    timezone: defaultTimezone
  });
  const timezoneItems = useMemo(
    () =>
      getTimezoneOptions(defaultTimezone).map((timezone) => ({
        key: timezone,
        label: formatTimezoneLabel(timezone)
      })),
    [defaultTimezone]
  );

  const queryClient = useQueryClient();
  const userProfileMutation = useUpdateUserProfile();

  useEffect(() => {
    if (user) {
      reset({
        email: user?.email,
        first_name: user?.firstName,
        last_name: user?.lastName,
        username: user?.username,
        timezone: defaultTimezone
      });
    }
  }, [user, reset, defaultTimezone]);

  const onSubmit = (data: UserProfileFormValues) => {
    if (!user) return;
    userProfileMutation.mutate(
      // A cleared timezone is left as it was rather than sent empty.
      { ...data, timezone: data.timezone || undefined, id: user.id },
      {
        onSuccess: () => {
          onOpenChange(false);
          queryClient.refetchQueries({ queryKey: ['user'] });
          addToast({
            color: 'success',
            title: 'User profile has been successfully updated.'
          });
        }
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 p-5 border-b border-gray-4">
              Edit account
            </ModalHeader>
            <ModalBody className="p-5">
              <form
                className="flex flex-col gap-5"
                id="user-profile-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Input
                  label="First name"
                  errorMessage={errors.first_name?.message}
                  isInvalid={!!errors.first_name}
                  {...register('first_name')}
                />

                <Input
                  label="Last name"
                  errorMessage={errors.last_name?.message}
                  isInvalid={!!errors.last_name}
                  {...register('last_name')}
                />
                <Input
                  label="Email"
                  errorMessage={errors.email?.message}
                  isInvalid={!!errors.email}
                  {...register('email')}
                />
                <Input
                  label="Username"
                  errorMessage={errors.username?.message}
                  isInvalid={!!errors.username}
                  {...register('username')}
                />
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      label="Timezone"
                      placeholder="Search timezone…"
                      description="Times we send you are shown in this timezone."
                      defaultItems={timezoneItems}
                      selectedKey={field.value ?? null}
                      onSelectionChange={(key) =>
                        field.onChange(key ? String(key) : '')
                      }
                      errorMessage={errors.timezone?.message}
                      isInvalid={!!errors.timezone}
                    >
                      {(item) => (
                        <AutocompleteItem key={item.key}>
                          {item.label}
                        </AutocompleteItem>
                      )}
                    </Autocomplete>
                  )}
                />
              </form>
            </ModalBody>
            <ModalFooter className="justify-start border-t border-gray-4 p-5">
              <ButtonV2
                form="user-profile-form"
                type="submit"
                isLoading={userProfileMutation.isPending}
                isDisabled={userProfileMutation.isPending}
              >
                Save
              </ButtonV2>
              <ButtonV2
                color="secondary"
                onPress={() => onClose()}
                isDisabled={userProfileMutation.isPending}
              >
                Close
              </ButtonV2>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AccountFormModal;
